<template>
  <div class="retirement-view-container">
    <MainHeader title="Retirement Planner">
      <template #left>
        <font-awesome-icon
          @click="router.back()"
          :icon="['fas', 'arrow-left-long']"
          class="back-arrow pointer"
          role="button"
          aria-label="Go back"
        />
      </template>
    </MainHeader>

    <div class="view content content-padding">
      <div class="ud-section">
        <div class="hero-strip blue">
          <div class="hero-icon-wrap blue">
            <font-awesome-icon :icon="['fas', 'umbrella-beach']" />
          </div>
          <div class="hero-text">
            <h2>Retirement Planner</h2>
            <p>
              Traditional retirement projection based on age, savings, and
              contributions.
            </p>
          </div>
        </div>

        <div v-if="!inputs.currentAge" class="wrapper-card empty-state">
          <font-awesome-icon :icon="['fas', 'user-clock']" class="empty-icon" />
          <p class="empty-title">Let's see where you stand</p>
          <p class="empty-sub">
            Add your age and savings to get a personalized retirement
            projection.
          </p>
        </div>

        <div class="wrapper-card">
          <h3 class="section-label">About you</h3>
          <div class="dual-input">
            <div class="ud-field flex-1">
              <label class="ud-label">Current age</label>
              <input
                v-model.number="inputs.currentAge"
                type="number"
                inputmode="decimal"
                class="input"
                placeholder="0"
              />
            </div>
            <div class="ud-field flex-1">
              <label class="ud-label">Retirement age</label>
              <input
                v-model.number="inputs.retirementAge"
                type="number"
                inputmode="decimal"
                class="input"
                placeholder="65"
              />
            </div>
          </div>
          <div class="dual-input">
            <div class="ud-field flex-1">
              <label class="ud-label">Life expectancy</label>
              <input
                v-model.number="inputs.lifeExpectancy"
                type="number"
                inputmode="decimal"
                class="input"
                placeholder="90"
              />
            </div>
            <div class="ud-field flex-1">
              <label class="ud-label">Current salary</label>
              <div class="input-with-prefix">
                <span class="prefix">$</span>
                <input
                  v-model.number="inputs.salary"
                  type="number"
                  inputmode="decimal"
                  class="input"
                  placeholder="0"
                />
              </div>
            </div>
          </div>
        </div>

        <div class="wrapper-card">
          <h3 class="section-label">Your savings</h3>
          <div class="ud-field">
            <label class="ud-label">Total retirement savings today</label>
            <div class="input-with-prefix">
              <span class="prefix">$</span>
              <input
                v-model.number="inputs.currentSavings"
                type="number"
                inputmode="decimal"
                class="input"
                placeholder="0"
              />
            </div>
            <p class="field-hint">
              401(k)s, IRAs, brokerage accounts. Not your emergency fund.
            </p>
          </div>

          <div class="dual-input">
            <div class="ud-field flex-1">
              <label class="ud-label">Monthly contribution</label>
              <div class="input-with-prefix">
                <span class="prefix">$</span>
                <input
                  v-model.number="inputs.monthlyContribution"
                  type="number"
                  inputmode="decimal"
                  class="input"
                  placeholder="0"
                />
              </div>
            </div>
            <div class="ud-field flex-1">
              <label class="ud-label">Employer match</label>
              <div class="input-with-suffix">
                <input
                  v-model.number="inputs.employerMatch"
                  type="number"
                  inputmode="decimal"
                  class="input"
                  placeholder="0"
                  step="0.1"
                />
                <span class="suffix">%</span>
              </div>
            </div>
          </div>

          <div class="ud-field checkbox-field">
            <label class="checkbox-row">
              <input type="checkbox" v-model="inputs.catchUp" />
              <span>I'm 50 or older (catch-up contributions)</span>
            </label>
            <p class="field-hint">
              Adds $7,500/year to 401(k) and $1,000/year to IRA limits.
            </p>
          </div>
        </div>

        <div class="wrapper-card">
          <h3 class="section-label">Income in retirement</h3>
          <div class="dual-input">
            <div class="ud-field flex-1">
              <label class="ud-label">Social Security (monthly)</label>
              <div class="input-with-prefix">
                <span class="prefix">$</span>
                <input
                  v-model.number="inputs.socialSecurity"
                  type="number"
                  inputmode="decimal"
                  class="input"
                  placeholder="0"
                />
              </div>
            </div>
            <div class="ud-field flex-1">
              <label class="ud-label">Other income (pension, etc)</label>
              <div class="input-with-prefix">
                <span class="prefix">$</span>
                <input
                  v-model.number="inputs.otherIncome"
                  type="number"
                  inputmode="decimal"
                  class="input"
                  placeholder="0"
                />
              </div>
            </div>
          </div>
          <p class="field-hint">
            Estimate at ssa.gov. The later you claim (up to 70), the higher your
            monthly benefit.
          </p>
        </div>

        <div class="wrapper-card">
          <h3 class="section-label">Spending needs</h3>
          <div class="ud-field">
            <label class="ud-label">Annual retirement expenses</label>
            <div class="input-with-prefix">
              <span class="prefix">$</span>
              <input
                v-model.number="inputs.retirementExpenses"
                type="number"
                inputmode="decimal"
                class="input"
                placeholder="0"
              />
            </div>
            <p class="field-hint">
              Most people need 70-80% of pre-retirement income. Healthcare often
              rises.
            </p>
          </div>
          <div class="dual-input">
            <div class="ud-field flex-1">
              <label class="ud-label">Expected return (before)</label>
              <div class="input-with-suffix">
                <input
                  v-model.number="inputs.preReturn"
                  type="number"
                  inputmode="decimal"
                  class="input"
                  placeholder="7"
                  step="0.1"
                />
                <span class="suffix">%</span>
              </div>
            </div>
            <div class="ud-field flex-1">
              <label class="ud-label">Expected return (after)</label>
              <div class="input-with-suffix">
                <input
                  v-model.number="inputs.postReturn"
                  type="number"
                  inputmode="decimal"
                  class="input"
                  placeholder="5"
                  step="0.1"
                />
                <span class="suffix">%</span>
              </div>
            </div>
          </div>
          <p class="field-hint">
            Returns typically drop in retirement as portfolios shift
            conservative.
          </p>
        </div>

        <div v-if="hasResults" class="wrapper-card results-card blue">
          <h3 class="section-label">Your retirement outlook</h3>
          <div class="outcome-verdict" :class="outlook.class">
            <font-awesome-icon :icon="outlook.icon" />
            <span>{{ outlook.message }}</span>
          </div>

          <div class="big-number">
            {{ formatCurrency(projection.atRetirement) }}
            <span class="big-number-sub"
              >at age {{ inputs.retirementAge || 65 }}</span
            >
          </div>

          <div class="retirement-stats">
            <div class="stat-row">
              <span class="stat-label">Years of savings</span>
              <span class="stat-value">{{ yearsUntilRetirement }}</span>
            </div>
            <div class="stat-row">
              <span class="stat-label">Total contributed</span>
              <span class="stat-value">{{
                formatCurrency(projection.totalContributed)
              }}</span>
            </div>
            <div class="stat-row">
              <span class="stat-label">Growth from returns</span>
              <span class="stat-value hint-good">{{
                formatCurrency(projection.growth)
              }}</span>
            </div>
            <div class="stat-row">
              <span class="stat-label">Annual need from savings</span>
              <span class="stat-value">{{
                formatCurrency(annualNeedFromSavings)
              }}</span>
            </div>
          </div>
        </div>

        <div v-if="hasResults" class="wrapper-card">
          <h3 class="section-label">Will it last?</h3>
          <div class="longevity-grid">
            <div class="longevity-card" :class="longevity.at80.class">
              <span class="longevity-age">Age 80</span>
              <span class="longevity-amount">{{
                formatCurrency(longevity.at80.balance)
              }}</span>
              <span class="longevity-status">{{ longevity.at80.status }}</span>
            </div>
            <div class="longevity-card" :class="longevity.at90.class">
              <span class="longevity-age">Age 90</span>
              <span class="longevity-amount">{{
                formatCurrency(longevity.at90.balance)
              }}</span>
              <span class="longevity-status">{{ longevity.at90.status }}</span>
            </div>
            <div class="longevity-card" :class="longevity.at100.class">
              <span class="longevity-age">Age 100</span>
              <span class="longevity-amount">{{
                formatCurrency(longevity.at100.balance)
              }}</span>
              <span class="longevity-status">{{ longevity.at100.status }}</span>
            </div>
          </div>

          <div v-if="projection.runsOut" class="afford-verdict bad">
            <font-awesome-icon :icon="['fas', 'circle-exclamation']" />
            <span
              >Your savings may run out around age
              {{ projection.depletionAge }}. Consider working longer, saving
              more, or reducing expenses.</span
            >
          </div>
          <div v-else class="afford-verdict good">
            <font-awesome-icon :icon="['fas', 'circle-check']" />
            <span
              >Your plan covers you to age {{ inputs.lifeExpectancy || 90 }}.
              You'd still have
              {{ formatCurrency(projection.finalBalance) }} left.</span
            >
          </div>
        </div>

        <div v-if="hasResults" class="wrapper-card">
          <h3 class="section-label">Year-by-year through retirement</h3>
          <div class="schedule-table">
            <div class="schedule-header">
              <span>Age</span>
              <span>Balance</span>
              <span>Withdrawal</span>
              <span>Status</span>
            </div>
            <div
              v-for="row in retirementSchedule"
              :key="row.age"
              class="schedule-row"
              :class="{
                'depletion-row': row.depleted,
                'milestone-row': row.milestone,
              }"
            >
              <span>{{ row.age }}</span>
              <span>{{ formatCurrency(row.balance) }}</span>
              <span>{{ formatCurrency(row.withdrawal) }}</span>
              <span>
                <span v-if="row.depleted" class="depleted-badge">Depleted</span>
                <span v-else-if="row.milestone" class="milestone-badge">{{
                  row.label
                }}</span>
                <span v-else class="hint-good">{{
                  formatCurrency(row.balance)
                }}</span>
              </span>
            </div>
          </div>
        </div>

        <div
          v-if="hasResults && gapAnalysis.gap > 0"
          class="wrapper-card warning-card"
        >
          <div class="warn-header">
            <font-awesome-icon :icon="['fas', 'triangle-exclamation']" />
            <span>Shortfall detected</span>
          </div>
          <p>
            You're projected to fall short by
            {{ formatCurrency(gapAnalysis.gap) }} at retirement. Here are ways
            to close the gap:
          </p>
          <div class="gap-options">
            <div class="gap-option">
              <span class="gap-label"
                >Save {{ formatCurrency(gapAnalysis.extraMonthly) }} more per
                month</span
              >
              <span class="gap-tag"
                >+{{ gapAnalysis.extraMonthlyPercent }}%</span
              >
            </div>
            <div class="gap-option">
              <span class="gap-label"
                >Work {{ gapAnalysis.extraYears }} more years</span
              >
              <span class="gap-tag"
                >Retire at
                {{ inputs.retirementAge + gapAnalysis.extraYears }}</span
              >
            </div>
            <div class="gap-option">
              <span class="gap-label"
                >Reduce spending by
                {{ formatCurrency(gapAnalysis.reduceSpending) }}/yr</span
              >
              <span class="gap-tag">−{{ gapAnalysis.reducePercent }}%</span>
            </div>
          </div>
        </div>

        <div class="wrapper-card">
          <h3 class="section-label">Compare scenarios</h3>
          <p class="section-blurb">
            See how different choices affect your outcome.
          </p>
          <div class="scenario-grid">
            <button
              v-for="s in scenarios"
              :key="s.name"
              type="button"
              class="scenario-card"
              :class="{ active: activeScenario === s.name }"
              @click="applyScenario(s)"
            >
              <span class="scenario-name">{{ s.name }}</span>
              <span class="scenario-desc">{{ s.desc }}</span>
            </button>
          </div>
          <div v-if="scenarioComparison" class="scenario-result">
            <div class="delta-row">
              <span>Retirement balance</span>
              <strong
                :class="scenarioComparison.balanceDelta > 0 ? 'good' : 'bad'"
              >
                {{ scenarioComparison.balanceDelta > 0 ? '+' : ''
                }}{{ formatCurrency(scenarioComparison.balanceDelta) }}
              </strong>
            </div>
            <div class="delta-row">
              <span>Lasts to age</span>
              <strong
                :class="scenarioComparison.longevityDelta > 0 ? 'good' : 'bad'"
              >
                {{ scenarioComparison.longevityDelta > 0 ? '+' : ''
                }}{{ scenarioComparison.longevityDelta }} yrs
              </strong>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import MainHeader from '../../components/View-Header.vue';

export default {
  name: 'CalculatorRetirement',
  components: { MainHeader },
  setup() {
    const router = useRouter();

    const inputs = ref({
      currentAge: null,
      retirementAge: 65,
      lifeExpectancy: 90,
      salary: null,
      currentSavings: null,
      monthlyContribution: null,
      employerMatch: null,
      catchUp: false,
      socialSecurity: null,
      otherIncome: null,
      retirementExpenses: null,
      preReturn: 7,
      postReturn: 5,
    });

    const activeScenario = ref(null);
    const scenarioSnapshot = ref(null);

    const hasResults = computed(() => {
      const age = inputs.value.currentAge;
      return age !== null && age > 0 && (inputs.value.currentSavings || 0) >= 0;
    });

    const yearsUntilRetirement = computed(() => {
      return Math.max(
        0,
        (inputs.value.retirementAge || 65) - (inputs.value.currentAge || 0)
      );
    });

    const totalMonthlyContribution = computed(() => {
      const base = inputs.value.monthlyContribution || 0;
      const matchPercent = (inputs.value.employerMatch || 0) / 100;
      const matchAmount = base * matchPercent;
      return base + matchAmount;
    });

    const annualContribution = computed(() => {
      return totalMonthlyContribution.value * 12;
    });

    const catchUpAmount = computed(() => {
      if (!inputs.value.catchUp) return 0;
      return 7500 + 1000;
    });

    const projection = computed(() => {
      if (!hasResults.value) {
        return {
          atRetirement: 0,
          totalContributed: 0,
          growth: 0,
          runsOut: false,
          depletionAge: null,
          finalBalance: 0,
        };
      }

      const r = (inputs.value.preReturn || 7) / 100;
      const years = yearsUntilRetirement.value;
      const baseContribution = annualContribution.value;
      const catchUp = catchUpAmount.value;
      let balance = inputs.value.currentSavings || 0;
      let totalContributed = balance;

      for (let year = 0; year < years; year++) {
        const yearlyTotal = baseContribution + catchUp;
        balance = balance * (1 + r) + yearlyTotal;
        totalContributed += yearlyTotal;
      }

      const growth = balance - totalContributed;

      const postR = (inputs.value.postReturn || 5) / 100;
      const expenses = inputs.value.retirementExpenses || 0;
      const ssAnnual = (inputs.value.socialSecurity || 0) * 12;
      const otherAnnual = (inputs.value.otherIncome || 0) * 12;
      const annualNeed = Math.max(0, expenses - ssAnnual - otherAnnual);

      let retirementBalance = balance;
      let depletionAge = null;
      const maxAge = inputs.value.lifeExpectancy || 90;
      const retirementAge = inputs.value.retirementAge || 65;

      for (let age = retirementAge; age <= maxAge; age++) {
        if (retirementBalance <= 0) {
          depletionAge = age;
          break;
        }
        retirementBalance = retirementBalance * (1 + postR) - annualNeed;
      }

      const runsOut = depletionAge !== null;
      const finalBalance = Math.max(0, retirementBalance);

      return {
        atRetirement: Math.round(balance),
        totalContributed: Math.round(totalContributed),
        growth: Math.round(growth),
        runsOut,
        depletionAge,
        finalBalance: Math.round(finalBalance),
      };
    });

    const annualNeedFromSavings = computed(() => {
      const expenses = inputs.value.retirementExpenses || 0;
      const ssAnnual = (inputs.value.socialSecurity || 0) * 12;
      const otherAnnual = (inputs.value.otherIncome || 0) * 12;
      return Math.max(0, expenses - ssAnnual - otherAnnual);
    });

    const outlook = computed(() => {
      if (!hasResults.value) {
        return {
          class: 'neutral',
          icon: ['fas', 'circle-question'],
          message: 'Enter your age and savings to see your outlook.',
        };
      }

      const p = projection.value;
      const need = annualNeedFromSavings.value;
      const yearsInRetirement =
        (inputs.value.lifeExpectancy || 90) -
        (inputs.value.retirementAge || 65);
      const roughNeeded = need * yearsInRetirement;

      if (p.atRetirement >= roughNeeded * 1.2) {
        return {
          class: 'good',
          icon: ['fas', 'circle-check'],
          message: "You're on track for a comfortable retirement.",
        };
      }
      if (p.atRetirement >= roughNeeded * 0.8) {
        return {
          class: 'caution',
          icon: ['fas', 'circle-exclamation'],
          message: "You're close but may need to adjust savings or timing.",
        };
      }
      return {
        class: 'bad',
        icon: ['fas', 'triangle-exclamation'],
        message: "You're projected to fall short. See options below.",
      };
    });

    const longevity = computed(() => {
      const postR = (inputs.value.postReturn || 5) / 100;
      const startBalance = projection.value.atRetirement;
      const annualNeed = annualNeedFromSavings.value;
      const retirementAge = inputs.value.retirementAge || 65;

      const checkAge = (targetAge) => {
        let balance = startBalance;
        for (let age = retirementAge; age < targetAge; age++) {
          balance = balance * (1 + postR) - annualNeed;
        }
        const ok = balance > 0;
        return {
          balance: Math.max(0, Math.round(balance)),
          class: ok ? 'good' : 'bad',
          status: ok ? 'On track' : 'Shortfall',
        };
      };

      return {
        at80: checkAge(80),
        at90: checkAge(90),
        at100: checkAge(100),
      };
    });

    const retirementSchedule = computed(() => {
      if (!hasResults.value) return [];

      const postR = (inputs.value.postReturn || 5) / 100;
      let balance = projection.value.atRetirement;
      const annualNeed = annualNeedFromSavings.value;
      const retirementAge = inputs.value.retirementAge || 65;
      const maxAge = inputs.value.lifeExpectancy || 90;
      const rows = [];

      const milestones = [
        { age: 65, label: 'Medicare' },
        { age: 67, label: 'Full SS' },
        { age: 70, label: 'Max SS' },
        { age: 73, label: 'RMDs start' },
      ];

      for (
        let age = retirementAge;
        age <= maxAge;
        age += age === retirementAge ? 1 : 5
      ) {
        if (age > retirementAge) {
          for (let a = age - 5; a < age; a++) {
            balance = balance * (1 + postR) - annualNeed;
          }
        }

        const milestone = milestones.find((m) => m.age === age);
        const depleted = balance <= 0;

        rows.push({
          age,
          balance: Math.max(0, Math.round(balance)),
          withdrawal: annualNeed,
          depleted,
          milestone: !!milestone,
          label: milestone ? milestone.label : null,
        });

        if (depleted) break;
      }

      return rows;
    });

    const gapAnalysis = computed(() => {
      if (!hasResults.value) {
        return {
          gap: 0,
          extraMonthly: 0,
          extraMonthlyPercent: 0,
          extraYears: 0,
          reduceSpending: 0,
          reducePercent: 0,
        };
      }

      const p = projection.value;
      const need = annualNeedFromSavings.value;
      const yearsInRetirement =
        (inputs.value.lifeExpectancy || 90) -
        (inputs.value.retirementAge || 65);
      const target = need * yearsInRetirement;
      const gap = Math.max(0, target - p.atRetirement);

      const years = yearsUntilRetirement.value;
      const r = (inputs.value.preReturn || 7) / 100;

      let extraMonthly = 0;
      if (gap > 0 && years > 0) {
        const fvAnnuityFactor = (Math.pow(1 + r, years) - 1) / r;
        extraMonthly = Math.ceil(gap / fvAnnuityFactor / 12);
      }

      const currentMonthly = inputs.value.monthlyContribution || 0;
      const extraMonthlyPercent =
        currentMonthly > 0
          ? Math.round((extraMonthly / currentMonthly) * 100)
          : 0;

      let extraYears = 0;
      if (gap > 0) {
        let testBalance = p.atRetirement;
        while (testBalance < target && extraYears < 20) {
          extraYears++;
          testBalance =
            testBalance * (1 + r) +
            (annualContribution.value + catchUpAmount.value);
        }
      }

      const reduceSpending = gap > 0 ? Math.ceil(gap / yearsInRetirement) : 0;
      const currentExpenses = inputs.value.retirementExpenses || 1;
      const reducePercent = Math.round(
        (reduceSpending / currentExpenses) * 100
      );

      return {
        gap,
        extraMonthly,
        extraMonthlyPercent,
        extraYears,
        reduceSpending,
        reducePercent,
      };
    });

    const scenarios = [
      {
        name: 'Work +2 years',
        desc: 'Delay to 67',
        apply: (i) => ({ ...i, retirementAge: (i.retirementAge || 65) + 2 }),
      },
      {
        name: 'Boost savings',
        desc: '+$200/mo',
        apply: (i) => ({
          ...i,
          monthlyContribution: (i.monthlyContribution || 0) + 200,
        }),
      },
      {
        name: 'Downsize',
        desc: '−20% expenses',
        apply: (i) => ({
          ...i,
          retirementExpenses: (i.retirementExpenses || 0) * 0.8,
        }),
      },
      {
        name: 'Part-time',
        desc: '$1,500/mo work',
        apply: (i) => ({ ...i, otherIncome: (i.otherIncome || 0) + 1500 }),
      },
      {
        name: 'Conservative',
        desc: '4% returns',
        apply: (i) => ({ ...i, preReturn: 4, postReturn: 3 }),
      },
    ];

    const applyScenario = (scenario) => {
      if (activeScenario.value === scenario.name) {
        activeScenario.value = null;
        scenarioSnapshot.value = null;
        return;
      }
      scenarioSnapshot.value = { ...inputs.value };
      activeScenario.value = scenario.name;
      inputs.value = scenario.apply(inputs.value);
    };

    const scenarioComparison = computed(() => {
      if (!scenarioSnapshot.value || !activeScenario.value) return null;

      const original = calculateForInputs(scenarioSnapshot.value);
      const current = calculateForInputs(inputs.value);

      return {
        balanceDelta: current.atRetirement - original.atRetirement,
        longevityDelta:
          (current.depletionAge || inputs.value.lifeExpectancy || 90) -
          (original.depletionAge || inputs.value.lifeExpectancy || 90),
      };
    });

    function calculateForInputs(i) {
      const r = (i.preReturn || 7) / 100;
      const years = Math.max(0, (i.retirementAge || 65) - (i.currentAge || 0));
      const baseContribution =
        (i.monthlyContribution || 0) * 12 * (1 + (i.employerMatch || 0) / 100);
      const catchUp = i.catchUp ? 8500 : 0;
      let balance = i.currentSavings || 0;

      for (let year = 0; year < years; year++) {
        balance = balance * (1 + r) + baseContribution + catchUp;
      }

      const postR = (i.postReturn || 5) / 100;
      const expenses = i.retirementExpenses || 0;
      const ssAnnual = (i.socialSecurity || 0) * 12;
      const otherAnnual = (i.otherIncome || 0) * 12;
      const annualNeed = Math.max(0, expenses - ssAnnual - otherAnnual);

      let retirementBalance = balance;
      let depletionAge = null;
      const maxAge = i.lifeExpectancy || 90;

      for (let age = i.retirementAge || 65; age <= maxAge; age++) {
        if (retirementBalance <= 0) {
          depletionAge = age;
          break;
        }
        retirementBalance = retirementBalance * (1 + postR) - annualNeed;
      }

      return { atRetirement: Math.round(balance), depletionAge };
    }

    function formatCurrency(n) {
      if (!isFinite(n) || n === null || n === undefined) return '$0';
      return '$' + Math.round(n).toLocaleString('en-US');
    }

    return {
      router,
      inputs,
      hasResults,
      yearsUntilRetirement,
      projection,
      annualNeedFromSavings,
      outlook,
      longevity,
      retirementSchedule,
      gapAnalysis,
      scenarios,
      activeScenario,
      applyScenario,
      scenarioComparison,
      formatCurrency,
    };
  },
};
</script>

<style scoped>
.retirement-view-container {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.back-arrow {
  font-size: 18px;
  color: var(--color-primary);
  cursor: pointer;
  transition: transform var(--transition-fast);
}
.back-arrow:hover {
  transform: translateX(-2px);
}
.dark-theme .back-arrow {
  color: var(--color-accent-teal);
}

.pointer {
  cursor: pointer;
}

.hero-strip {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px;
  background: linear-gradient(135deg, #eef4ff 0%, #f8fbff 100%);
  border-radius: var(--radius-lg);
  margin-bottom: 16px;
}
.hero-strip.blue {
  background: linear-gradient(135deg, #dbeafe 0%, #eff6ff 100%);
}
.hero-icon-wrap {
  width: 48px;
  height: 48px;
  border-radius: var(--radius-md);
  background: var(--color-accent-purple);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.hero-icon-wrap.blue {
  background: #3b82f6;
}
.hero-icon-wrap font-awesome-icon {
  color: white;
  font-size: 24px;
}
.hero-text h2 {
  font-family: 'Poppins', sans-serif;
  font-size: 18px;
  font-weight: 600;
  color: var(--color-deep-teal, #004751);
  margin: 0 0 4px 0;
}
.hero-text p {
  font-size: 13px;
  color: #666;
  margin: 0;
  line-height: 1.4;
}

.ud-section {
  padding: 16px;
  padding-bottom: 80px;
}

.wrapper-card {
  background: white;
  border-radius: var(--radius-lg);
  padding: 16px;
  margin-bottom: 16px;
  border: 1px solid #f0f0f0;
}
.dark-theme .wrapper-card {
  background: #212e3e;
  border-color: #2a3a4e;
}

.empty-state {
  text-align: center;
  padding: 2rem 1.5rem;
}
.empty-icon {
  font-size: 36px;
  color: #3b82f6;
  margin-bottom: 1rem;
}
.empty-title {
  font-size: 0.95rem;
  font-weight: 700;
  margin: 0 0 0.4rem;
}
.light-theme .empty-title {
  color: var(--color-primary);
}
.dark-theme .empty-title {
  color: var(--color-teal-mint);
}
.empty-sub {
  font-size: 0.78rem;
  margin: 0;
}

.light-theme .empty-sub {
  color: #6b7280;
}

.section-label {
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: var(--color-deep-teal, #004751);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 12px 0;
}
.section-blurb {
  font-size: 13px;
  margin: 0 0 16px 0;
  line-height: 1.4;
}

.light-theme .section-blurb {
  color: #666;
}

.ud-field {
  margin-bottom: 14px;
}
.ud-field:last-child {
  margin-bottom: 0;
}
.ud-label {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: #555;
  margin-bottom: 6px;
}

.input-with-prefix,
.input-with-suffix {
  position: relative;
  display: flex;
  align-items: center;
}
.input-with-prefix .input {
  padding-left: 28px;
}
.input-with-suffix .input {
  padding-right: 32px;
}
.prefix,
.suffix {
  position: absolute;
  color: #999;
  font-size: 14px;
  pointer-events: none;
}
.prefix {
  left: 12px;
}
.suffix {
  right: 12px;
}

.input {
  width: 100%;
  padding: 12px 14px;
  border: 1.5px solid #e5e5e5;
  border-radius: var(--radius-md);
  font-family: 'Poppins', sans-serif;
  font-size: 15px;
  color: var(--color-primary, #213547);
  background: white;
  outline: none;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;
}
.input:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}
.dark-theme .input {
  background: #1a2535;
  border-color: #2a3a4e;
  color: white;
}

.dual-input {
  display: flex;
  gap: 10px;
}
.flex-1 {
  flex: 1;
}

.field-hint {
  font-size: 12px;
  color: #888;
  margin: 6px 0 0 0;
  line-height: 1.4;
}
.hint-good {
  color: var(--color-accent-green, #5dcaa5);
}

.checkbox-field {
  padding: 8px 0;
}
.checkbox-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 14px;
  cursor: pointer;
  line-height: 1.4;
}
.checkbox-row input {
  margin-top: 2px;
}

.results-card {
  background: linear-gradient(135deg, #dbeafe 0%, #eff6ff 100%);
  border: 1px solid #bfdbfe;
}
.results-card.blue {
  background: linear-gradient(135deg, #dbeafe 0%, #eef4ff 100%);
}
.dark-theme .results-card {
  background: linear-gradient(135deg, #0d1a30 0%, #1e1a40 100%);
  border-color: #1e3a5f;
}

.outcome-verdict {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  border-radius: var(--radius-md);
  font-size: 14px;
  margin-bottom: 16px;
  background: white;
  line-height: 1.4;
}
.outcome-verdict font-awesome-icon {
  font-size: 20px;
  flex-shrink: 0;
}
.outcome-verdict.good {
  color: var(--color-accent-green, #5dcaa5);
  border-left: 3px solid var(--color-accent-green);
}
.outcome-verdict.caution {
  color: #e2a53c;
  border-left: 3px solid #e2a53c;
}
.outcome-verdict.bad {
  color: #d45a4a;
  border-left: 3px solid #d45a4a;
}
.outcome-verdict.neutral {
  color: #888;
}

.big-number {
  font-family: 'Poppins', sans-serif;
  font-size: 32px;
  font-weight: 600;
  color: var(--color-deep-teal, #004751);
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 16px;
}
.big-number-sub {
  font-size: 13px;
  font-weight: 400;
  color: #888;
}

.retirement-stats {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.stat-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  font-size: 14px;
}
.stat-row:last-child {
  border-bottom: none;
}
.stat-label {
  color: #555;
}
.stat-value {
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  color: var(--color-deep-teal, #004751);
}

.longevity-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 10px;
  margin-bottom: 16px;
}
.longevity-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 12px;
  background: #f8f8f8;
  border-radius: var(--radius-md);
  text-align: center;
}
.longevity-card.good {
  background: var(--teal-50, #e5f7f1);
}
.longevity-card.bad {
  background: #fdeee7;
}
.dark-theme .longevity-card {
  background: #1a2535;
}
.dark-theme .longevity-card.good {
  background: #0d2420;
}
.dark-theme .longevity-card.bad {
  background: #2e1d18;
}
.longevity-age {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #888;
}
.longevity-amount {
  font-family: 'Poppins', sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: var(--color-deep-teal, #004751);
}
.longevity-status {
  font-size: 11px;
  font-weight: 600;
}
.longevity-card.good .longevity-status {
  color: var(--color-accent-green, #5dcaa5);
}
.longevity-card.bad .longevity-status {
  color: #d45a4a;
}

.afford-verdict {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  border-radius: var(--radius-md);
  font-size: 14px;
  background: white;
  line-height: 1.4;
}
.afford-verdict font-awesome-icon {
  font-size: 20px;
  flex-shrink: 0;
}
.afford-verdict.good {
  color: var(--color-accent-green, #5dcaa5);
  border-left: 3px solid var(--color-accent-green);
}
.afford-verdict.bad {
  color: #d45a4a;
  border-left: 3px solid #d45a4a;
}

.schedule-table {
  margin-top: 12px;
  max-height: 320px;
  overflow-y: auto;
  border: 1px solid #f0f0f0;
  border-radius: var(--radius-sm);
}
.schedule-header,
.schedule-row {
  display: grid;
  grid-template-columns: 50px 1fr 1fr 90px;
  font-size: 12px;
  padding: 8px 10px;
  border-bottom: 1px solid #f5f5f5;
}
.schedule-header {
  background: #eef4ff;
  font-weight: 600;
  color: var(--color-deep-teal, #004751);
  position: sticky;
  top: 0;
}
.dark-theme .schedule-header {
  background: #0d1a30;
}
.schedule-row.depletion-row {
  background: #fdeee7;
  font-weight: 500;
}
.dark-theme .schedule-row.depletion-row {
  background: #2e1d18;
}
.schedule-row.milestone-row {
  background: var(--teal-50, #e5f7f1);
  font-weight: 500;
}

.depleted-badge {
  background: #d45a4a;
  color: white;
  font-size: 10px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: var(--radius-pill);
  text-transform: uppercase;
}
.milestone-badge {
  background: #3b82f6;
  color: white;
  font-size: 10px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: var(--radius-pill);
  text-transform: uppercase;
}

.warning-card {
  background: #fff8ee;
  border-left: 3px solid #e2a53c;
}
.dark-theme .warning-card {
  background: #2e2718;
  border-left-color: #e2a53c;
}
.warn-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  color: #c97b2c;
  margin-bottom: 6px;
}
.warn-header font-awesome-icon {
  font-size: 20px;
}
.warning-card p {
  margin: 0 0 12px 0;
  font-size: 13px;
  color: #555;
  line-height: 1.5;
}

.gap-options {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.gap-option {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  background: white;
  border-radius: var(--radius-md);
  border: 1px solid #f0f0f0;
}
.dark-theme .gap-option {
  background: #212e3e;
  border-color: #2a3a4e;
}
.gap-label {
  font-size: 13px;
  color: #555;
}
.gap-tag {
  font-size: 11px;
  font-weight: 700;
  color: var(--color-accent-purple);
  background: var(--purple-50);
  padding: 3px 8px;
  border-radius: var(--radius-pill);
}

.scenario-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-bottom: 12px;
}
.scenario-card {
  padding: 12px;
  background: #f8f8f8;
  border-radius: var(--radius-md);
  border: 1.5px solid transparent;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;
  font-family: 'Poppins', sans-serif;
}
.scenario-card:hover {
  background: #eef4ff;
}
.scenario-card.active {
  border-color: #3b82f6;
  background: #dbeafe;
}
.dark-theme .scenario-card {
  background: #1a2535;
}
.dark-theme .scenario-card.active {
  background: #0d1a30;
  border-color: #3b82f6;
}
.scenario-name {
  display: block;
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 2px;
}
.light-theme .scenario-name {
  color: var(--color-deep-teal, #004751);
}
.dark-theme .scenario-name {
  color: var(--color-teal-mint, #004751);
}
.scenario-desc {
  display: block;
  font-size: 11px;
}

.light-theme .scenario-desc {
  color: #888;
}

.dark-theme .scenario-desc {
color: white;
}

.scenario-result {
  background: white;
  border: 1px solid #e5e5e5;
  border-radius: var(--radius-md);
  padding: 12px;
}
.dark-theme .scenario-result {
  background: #212e3e;
  border-color: #2a3a4e;
}
.delta-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  font-size: 14px;
}
.delta-row span {
  color: #555;
}
.delta-row strong {
  font-family: 'Poppins', sans-serif;
  font-size: 16px;
}
.delta-row strong.good {
  color: var(--color-accent-green, #5dcaa5);
}
.delta-row strong.bad {
  color: #d45a4a;
}
</style>
